import xhrService from '../xhrService';
import { isNetworkFailure } from '../offlineFallback';
import { ProjectFolderFormDataType } from '../../components/Projects/Details/ProjectSitePhotos';
const baseUrl = '/api/divisions';

/**
 * SW2-2458: the upload went out and the connection dropped before the answer.
 * The server may have saved the photos, and nothing on it can tell them apart
 * from a second copy (no client id or dedupe), so the Retry offered by the
 * upload notice warns instead of being silent. The notice shows
 * `response.data.message`; a request that never left (no `request`) is not
 * affected.
 */
function sentButUnconfirmed(err: any) {
  if (!isNetworkFailure(err)) return err;

  const unconfirmed: any = new Error('Upload not confirmed.');

  unconfirmed.request = err.request;
  // The notice's Discard prompt reads this: some photos may be on the server.
  unconfirmed.uncertain = true;
  unconfirmed.response = {
    data: {
      message:
        'The connection dropped before the upload was confirmed. Some may already be in the folder: check it before Retry.',
    },
  };

  return unconfirmed;
}

export const projectSitePhotos = {
  indexFolder: ({
    projectId,
    divisionId,
  }: {
    projectId: number;
    divisionId: number;
  }) => {
    if (!divisionId || !projectId) {
      return false;
    }

    return xhrService
      .get(`${baseUrl}/${divisionId}/projects/${projectId}/sitephotos/index`)
      .then((response) => response.data)
      .catch((e) => {
        console.log(e.message);

        return false;
      });
  },

  createFolder: ({
    projectId,
    divisionId,
    formData,
    userId,
  }: {
    projectId: number;
    divisionId: number;
    formData: ProjectFolderFormDataType;
    userId: number;
  }) => {
    if (!divisionId || !projectId) {
      return false;
    }

    return xhrService
      .post(
        `${baseUrl}/${divisionId}/projects/${projectId}/sitephotos/create`,
        {
          userId,
          formData,
        },
      )
      .then((response) => response.data)
      .catch((e) => {
        console.log(e.response.data);

        return e.response.data;
      });
  },

  updateFolder: ({
    projectId,
    divisionId,
    formData,
    userId,
  }: {
    projectId: number;
    divisionId: number;
    formData: ProjectFolderFormDataType;
    userId: number;
  }) => {
    if (!divisionId || !projectId) {
      return false;
    }

    return xhrService
      .post(
        `${baseUrl}/${divisionId}/projects/${projectId}/sitephotos/update`,
        {
          userId,
          formData,
        },
      )
      .then((response) => response.data)
      .catch((e) => {
        console.log(e.message);

        return false;
      });
  },

  deleteFolder: ({
    projectId,
    divisionId,
    folderId,
  }: {
    projectId: number;
    divisionId: number;
    folderId: number;
  }) => {
    if (!divisionId || !projectId) {
      return false;
    }

    return xhrService
      .post(
        `${baseUrl}/${divisionId}/projects/${projectId}/sitephotos/delete`,
        { folderId },
      )
      .then((response) => response.data)
      .catch((e) => {
        console.log(e.message);

        return false;
      });
  },

  uploadFiles: ({
    projectId,
    divisionId,
    formData,
    onProgress,
    throwOnError = false,
    background = false,
  }: {
    projectId: number;
    divisionId: number;
    formData: any;
    /** SW2-2458: upload progress, 0–1. */
    onProgress?: (fraction: number) => void;
    /** SW2-2458: a background job needs the real error to offer Retry. */
    throwOnError?: boolean;
    /** SW2-2458: a background-queue upload, which foreground requests do not wait for. */
    background?: boolean;
  }) => {
    if (!divisionId || !projectId) {
      return false;
    }

    return xhrService
      .post(
        `${baseUrl}/${divisionId}/projects/${projectId}/sitephotos/files/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: onProgress
            ? (e: ProgressEvent) => {
                if (e.total) onProgress(e.loaded / e.total);
              }
            : undefined,
          ...(background ? { backgroundUpload: true } : {}),
        } as any,
      )
      .then((response) => response.data)
      .catch((e) => {
        if (throwOnError) throw sentButUnconfirmed(e);
        console.log(e.message);

        return false;
      });
  },

  getFiles: ({
    projectId,
    divisionId,
    folderId,
  }: {
    projectId: number;
    divisionId: number;
    folderId: number;
  }) => {
    if (!divisionId || !projectId) {
      return false;
    }

    return xhrService
      .get(
        `${baseUrl}/${divisionId}/projects/${projectId}/sitephotos/${folderId}/files/list`,
      )
      .then((response) => response.data)
      .catch((e) => {
        console.log(e.message);

        return false;
      });
  },

  deleteFile: ({
    projectId,
    divisionId,
    folderId,
    fileId,
  }: {
    projectId: number;
    divisionId: number;
    folderId: number;
    fileId: number;
  }) => {
    if (!divisionId || !projectId) {
      return false;
    }

    return xhrService
      .post(
        `${baseUrl}/${divisionId}/projects/${projectId}/sitephotos/${folderId}/files/delete`,
        { fileId },
      )
      .then((response) => response.data)
      .catch((e) => {
        console.log(e.message);

        return false;
      });
  },

  updateFile: ({
    projectId,
    divisionId,
    folderId,
    fileId,
    formData,
  }: {
    projectId: number;
    divisionId: number;
    folderId: number;
    fileId: number;
    formData: any;
  }) => {
    if (!divisionId || !projectId) {
      return false;
    }

    return xhrService
      .post(
        `${baseUrl}/${divisionId}/projects/${projectId}/sitephotos/${folderId}/files/${fileId}/update`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      .then((response) => response.data)
      .catch((e) => {
        console.log(e.message);

        return false;
      });
  },
};

export default projectSitePhotos;
